export const rhPayeGuide = {
  id: 'rh-paie',
  title: 'Guide RH / Paie',
  subtitle: 'Gestion des employés, pointages, paie, planification et outils analytiques pour les responsables RH.',
  sections: [
    /* ============================================================
       1. TABLEAU DE BORD
    ============================================================ */
    {
      id: 'tableau-de-bord',
      title: 'Tableau de bord',
      overview: 'Votre centre de commande quotidien : présence en temps reel, alertes et actions prioritaires.',
      content: [
        {
          id: 'comprendre-dashboard',
          title: 'Comprendre le tableau de bord',
          description: 'Le tableau de bord affiche en temps reel la situation du jour : qui est présent, absent, en retard ou en pause. Les KPIs heros (Présents/Absents) sont mis en evidence, suivis de KPIs secondaires (Attendus, Retards, En pause).',
          whenToUse: 'Consultez le tableau de bord chaque matin des votre connexion pour avoir une vue instantanee de la journee.',
          steps: [
            'Connectez-vous a PunchTM. Le tableau de bord est la page d\'accueil par défaut.',
            'Les KPIs heros en haut montrent le nombre d\'employés Présents (vert) et Absents (rouge).',
            'Les KPIs secondaires montrent : Attendus, Retards, En pause.',
            'La section "Actions prioritaires" affiche les alertes en cours qui necessitent votre attention.',
            'Le tableau de présence liste tous les employés avec leur statut en temps reel.',
            'Les derniers pointages s\'affichent en bas avec l\'heure et le type d\'evenement.'
          ],
          screenshot: '/docs/dashboard-overview.png',
          screenshotAlt: 'Tableau de bord PunchTM',
          result: 'Vous savez en un coup d\'oeil combien d\'employés sont présents, absents et quelles alertes traiter.',
          tips: [
            'Utilisez les filtres dans le header (site, zone, quart, statut) pour affiner la vue selon votre perimetre.',
            'Cliquez sur le nom d\'un employé dans le tableau pour ouvrir sa fiche détaillée.',
            'Le bouton "Rafraichir" met a jour les donnees sans recharger la page.'
          ]
        },
        {
          id: 'filtres-dashboard',
          title: 'Filtres et recherche',
          description: 'Les filtres globaux en haut du tableau de bord vous permettent de restreindre la vue a un site, une zone, un quart ou un statut spécifique.',
          whenToUse: 'Lorsque vous gerez plusieurs sites ou zones et que vous voulez voir uniquement votre perimetre.',
          steps: [
            'Dans le header du dashboard, utilisez la barre de recherche pour chercher un employé par nom ou code.',
            'Sélectionnez un Site dans le premier filtre deroulant pour ne voir que les employés de ce site.',
            'Sélectionnez une Zone pour affiner davantage.',
            'Sélectionnez un Quart pour filtrer par horaire.',
            'Sélectionnez un Statut (Présent, Absent, Retard, Pause) pour ne voir que cette catégorie.',
            'Les filtres se combinent : vous pouvez sélectionner un site ET un quart en meme temps.'
          ],
          result: 'L\'affichage est filtre instantanement. Les KPIs se recalculent pour ne refleter que la sélection.',
          tips: ['Pour reinitialiser tous les filtres, sélectionnez "Tous" dans chaque filtre deroulant.']
        },
        {
          id: 'actions-prioritaires',
          title: 'Actions prioritaires',
          description: 'Cette section n\'apparait que lorsqu\'il y a des alertes en cours (retards, absences non justifiées, anomalies de pointage). Chaque alerte est cliquable pour un traitement rapide.',
          whenToUse: 'Traitez les actions prioritaires des qu\'elles apparaissent pour maintenir un suivi rigoureux.',
          steps: [
            'Repérez la section "Actions prioritaires" sous les KPIs (elle est masquee s\'il n\'y a aucune alerte).',
            'Chaque alerte affiche : type (retard, absence, anomalie), employé concerne, heure et site/zone.',
            'Cliquez sur une alerte pour ouvrir le détail et choisir une action (justifier, traiter, ignorer).',
            'Les alertes traitées disparaissent automatiquement de la liste.'
          ],
          result: 'Les alertes sont traitées et tracees. L\'historique est conserve dans le module Alertes.',
          warnings: ['Les alertes non traitées s\'accumulent et faussent les statistiques. Traitez-les quotidiennement.']
        }
      ]
    },

    /* ============================================================
       2. GESTION DES EMPLOYES
    ============================================================ */
    {
      id: 'gestion-employés',
      title: 'Gestion des employés',
      overview: 'Centre RH complet : creation, modification, import CSV, actions en masse et fiches detaillees.',
      content: [
        {
          id: 'liste-employés',
          title: 'Liste des employés',
          description: 'La page Employés affiche tous les employés avec des KPIs (total, actifs, inactifs, nouveaux), des filtres avances et un tri par colonnes.',
          whenToUse: 'Pour consulter, rechercher ou gerer les employés de votre organisation.',
          steps: [
            'Cliquez sur "Employés" dans le menu lateral.',
            'Les KPIs en haut montrent : Total, Actifs, Inactifs et Nouveaux (ce mois).',
            'Utilisez la barre de recherche pour trouver un employé par nom, prenom ou code.',
            'Filtrez par Zone, Quart ou Statut (Actifs/Inactifs) via les menus deroulants.',
            'Cliquez sur l\'en-tete d\'une colonne pour trier (Code, Nom, Poste, etc.).',
            'Cliquez sur la ligne d\'un employé pour ouvrir son panneau de détails a droite.'
          ],
          screenshot: '/docs/employees-list.png',
          screenshotAlt: 'Liste des employés avec KPIs et filtres',
          result: 'Vous visualisez la liste complete de vos employés avec la possibilite de filtrer et trier selon vos besoins.'
        },
        {
          id: 'créer-employé',
          title: 'Créer un employé',
          description: 'Ajoutez un nouvel employé manuellement en renseignant ses informations personnelles et professionnelles.',
          whenToUse: 'Pour l\'embauche d\'un nouvel employé. Pour un onboarding massif, privilegiez l\'import CSV.',
          steps: [
            'Sur la page Employés, cliquez sur le bouton "+ Ajouter" en haut a droite.',
            'Remplissez les champs obligatoires : Code employé (5 chiffres), Prenom, Nom.',
            'Remplissez les champs optionnels : Email, Telephone, Poste, Departement.',
            'Sélectionnez le Site, la Zone et le Quart de travail.',
            'Indiquez la date d\'embauche.',
            'Cliquez sur "Créer" pour enregistrer.'
          ],
          screenshot: '/docs/employees-create.png',
          screenshotAlt: 'Formulaire de creation d\'employé',
          result: 'L\'employé est créé et apparait dans la liste. Il peut immédiatement pointer au kiosque avec son code a 5 chiffres.',
          tips: [
            'Le code employé doit etre unique et comporter exactement 5 chiffres (ex: 10001, 20015).',
            'L\'email n\'est pas obligatoire mais recommande pour le suivi RH.',
            'Si vous ne sélectionnez pas de site/zone/quart, l\'employé ne sera affecte a aucun perimetre.'
          ],
          errors: [
            { problem: 'Erreur "Code employé déjà utilise"', solution: 'Ce code existe déjà. Choisissez un code different. Consultez la liste pour voir les codes existants.' },
            { problem: 'Erreur "Code invalide"', solution: 'Le code doit contenir exactement 5 chiffres (pas de lettres, pas d\'espaces).' }
          ]
        },
        {
          id: 'modifier-employé',
          title: 'Modifier un employé',
          description: 'Modifiez les informations d\'un employé existant : poste, département, site, zone, quart, etc.',
          whenToUse: 'Lorsqu\'un employé change de poste, de site, de quart ou en cas de correction d\'informations.',
          steps: [
            'Trouvez l\'employé dans la liste (recherche ou filtres).',
            'Cliquez sur l\'icone crayon (Modifier) dans la colonne Actions.',
            'Le formulaire d\'edition s\'ouvre avec les informations actuelles pre-remplies.',
            'Modifiez les champs nécessaires.',
            'Cliquez sur "Enregistrer" pour sauvegarder.'
          ],
          result: 'Les modifications sont appliquees immédiatement. L\'employé verra ses nouvelles affectations des son prochain pointage.',
          warnings: ['Le code employé ne peut pas etre modifié. Si un code est errone, desactivez l\'employé et créez-en un nouveau avec le bon code.']
        },
        {
          id: 'desactiver-employé',
          title: 'Desactiver / Réactiver un employé',
          description: 'La desactivation empeche un employé de pointer au kiosque sans supprimer son historique. La réactivation restaure son accès.',
          whenToUse: 'Depart d\'un employé, conge longue duree, fin de contrat. Reactivation en cas de retour.',
          steps: [
            'Trouvez l\'employé et ouvrez son panneau de détails.',
            'Cliquez sur le bouton "Desactiver" (rouge).',
            'Confirmez l\'action dans la boite de dialogue.',
            'L\'employé passe au statut "Inactif" et n\'apparait plus dans les filtres par défaut.',
            'Pour réactiver : filtrez par "Inactifs", trouvez l\'employé et cliquez sur "Réactiver".'
          ],
          result: 'Desactivation : l\'employé ne peut plus pointer. Son historique est conserve. Reactivation : l\'employé peut a nouveau pointer.',
          tips: ['Utilisez le filtre "Actifs/Inactifs" pour retrouver les employés desactives.'],
          warnings: ['La desactivation n\'est pas une suppression. L\'historique de pointage et les donnees de paie sont toujours accessibles.']
        },
        {
          id: 'import-csv',
          title: 'Import CSV (onboarding massif)',
          description: 'Importez plusieurs employés en une seule opération via un fichier CSV. Le processus en 3 étapes garantit un import sécurisé avec prévisualisation avant validation.',
          whenToUse: 'Pour l\'onboarding initial de nombreux employés ou l\'intégration de donnees depuis un systeme externe.',
          steps: [
            'Sur la page Employés, cliquez sur le bouton "Import CSV" (icone upload).',
            'ETAPE 1 - Telechargement du modele : Cliquez sur "Telecharger le modele CSV" pour obtenir le fichier de référence avec les colonnes requises.',
            'Remplissez le fichier CSV avec les donnees de vos employés (Code, Prenom, Nom sont obligatoires).',
            'Sélectionnez votre fichier CSV rempli en cliquant sur la zone de depot.',
            'Cliquez sur "Prévisualiser" pour passer a l\'étape 2.',
            'ETAPE 2 - Prévisualisation : Le systeme affiche un tableau de validation ligne par ligne.',
            'Les badges en haut indiquent : Valides (vert), Doublons (ambre), Erreurs (rouge).',
            'Chaque ligne montre le statut, le code, le nom, le poste, le site, la zone, le quart et les détails des erreurs.',
            'Les sites, zones et quarts sont automatiquement resolus par nom (pas besoin de connaitre les IDs).',
            'Vérifiez les donnees puis cliquez sur "Importer N employé(s)" pour confirmer.',
            'ETAPE 3 - Résultat : Le systeme affiche le nombre d\'employés créés et ignores.',
            'Fermez le dialogue. La liste des employés est automatiquement rafraichie.'
          ],
          screenshot: '/docs/employees-import-csv.png',
          screenshotAlt: 'Import CSV - Étape de prévisualisation',
          result: 'Les employés sont créés en masse. Les doublons sont automatiquement ignores. L\'action est journalisee.',
          tips: [
            'Le modele CSV inclut des lignes d\'exemple et des commentaires listant les sites, zones et quarts disponibles.',
            'Les noms de sites, zones et quarts dans le CSV doivent correspondre exactement aux noms configures dans PunchTM.',
            'Les lignes commencant par # sont ignorees (commentaires).',
            'Les lignes vides sont automatiquement ignorees.'
          ],
          errors: [
            { problem: 'Erreur "Code invalide" dans la prévisualisation', solution: 'Le code doit etre exactement 5 chiffres. Corrigez votre CSV et relancez.' },
            { problem: 'Doublon détecté', solution: 'Un employé avec ce code existe déjà. Il sera ignore lors de l\'import (pas de duplication).' },
            { problem: 'Site/Zone/Quart non resolu', solution: 'Le nom dans le CSV ne correspond a aucun site/zone/quart existant. Vérifiez l\'orthographe exacte.' }
          ]
        },
        {
          id: 'actions-masse-employés',
          title: 'Actions en masse',
          description: 'Sélectionnez plusieurs employés pour effectuer des opérations groupees : assignation de zone, quart, desactivation, etc.',
          whenToUse: 'Lorsque vous devez effectuer la meme modification sur plusieurs employés (changement de zone, reassignation de quart, etc.).',
          steps: [
            'Cochez les cases a gauche de chaque employé que vous souhaitez sélectionner.',
            'Utilisez la case "Tout sélectionner" en haut pour sélectionner toute la page.',
            'Une barre d\'actions apparait avec le nombre d\'employés sélectionnés.',
            'Choisissez l\'action souhaitee : Assigner zone, Assigner quart, Desactiver, etc.',
            'Confirmez l\'action dans la boite de dialogue.',
            'Les modifications sont appliquees a tous les employés sélectionnés.'
          ],
          result: 'L\'action est appliquee a tous les employés sélectionnés en une seule opération.',
          warnings: ['Vérifiez bien votre sélection avant de confirmer une action en masse, surtout pour la desactivation.']
        },
        {
          id: 'fiche-employé',
          title: 'Fiche employé détaillée',
          description: 'Le panneau lateral droit affiche les détails complets d\'un employé : informations personnelles, statistiques de pointage et historique.',
          whenToUse: 'Pour consulter les détails d\'un employé spécifique ou vérifier ses statistiques.',
          steps: [
            'Cliquez sur la ligne d\'un employé dans la liste.',
            'Le panneau s\'ouvre a droite avec les informations completes.',
            'Informations : code, nom, prenom, email, telephone, poste, département.',
            'Affectation : site, zone, quart de travail.',
            'Statistiques : nombre de jours travailles, heures, retards, absences.',
            'Depuis ce panneau, vous pouvez directement modifier ou desactiver l\'employé.'
          ],
          result: 'Vous avez une vue 360° de l\'employé en un clic.'
        }
      ]
    },

    /* ============================================================
       3. SITES ET ZONES
    ============================================================ */
    {
      id: 'sites-zones',
      title: 'Sites et Zones',
      overview: 'Gerez la structuré geographique de votre organisation : sites de travail et zones au sein de chaque site.',
      content: [
        {
          id: 'gerer-sites',
          title: 'Gerer les sites',
          description: 'Un site représente un lieu physique de travail (usine, bureau, entrepot). Chaque site peut contenir plusieurs zones.',
          whenToUse: 'Lors de la configuration initiale ou lorsque vous ajoutez un nouveau lieu de travail.',
          steps: [
            'Cliquez sur "Sites & Zones" dans le menu lateral.',
            'Les KPIs en haut montrent le nombre de sites et zones.',
            'Pour créer un site : cliquez sur "+ Ajouter un site", renseignez le nom et l\'adresse.',
            'Pour modifier : cliquez sur l\'icone crayon.',
            'Pour supprimer : cliquez sur l\'icone corbeille (bloque si des employés sont assignes).'
          ],
          result: 'Le site est créé et disponible pour l\'assignation d\'employés et la creation de zones.',
          warnings: ['Un site ne peut pas etre supprimé s\'il a des employés assignes. Reassignez d\'abord les employés.'],
          errors: [
            { problem: 'Suppression bloquee', solution: 'Des employés sont encore assignes a ce site. Reassignez-les a un autre site avant de supprimer.' }
          ]
        },
        {
          id: 'gerer-zones',
          title: 'Gerer les zones',
          description: 'Une zone est une subdivision d\'un site (ex: Atelier A, Bureau comptabilite, Entrepot Nord). Les zones permettent un suivi plus fin des employés.',
          whenToUse: 'Pour organiser les employés au sein d\'un site par secteur ou département.',
          steps: [
            'Dans la page Sites & Zones, sélectionnez un site.',
            'Cliquez sur "+ Ajouter une zone".',
            'Renseignez le nom de la zone.',
            'La zone est automatiquement rattachee au site sélectionné.',
            'Assignez des employés a cette zone depuis la fiche employé ou via les actions en masse.'
          ],
          result: 'La zone est créée et les employés peuvent y etre assignes pour un suivi plus precis.',
          tips: ['Utilisez des noms de zones clairs et coherents pour faciliter le filtrage et les rapports.']
        }
      ]
    },

    /* ============================================================
       4. QUARTS DE TRAVAIL
    ============================================================ */
    {
      id: 'quarts-travail',
      title: 'Quarts de travail',
      overview: 'Definissez les horaires de travail (quarts/shifts) utilises dans votre organisation.',
      content: [
        {
          id: 'gerer-quarts',
          title: 'Créer et gerer les quarts',
          description: 'Un quart de travail definit une plage horaire (ex: Jour 7h-15h, Soir 15h-23h, Nuit 23h-7h). Les quarts sont assignes aux employés pour le suivi des retards et absences.',
          whenToUse: 'Lors de la configuration initiale ou lorsque de nouveaux horaires sont introduits.',
          steps: [
            'Cliquez sur "Quarts" dans le menu lateral.',
            'La liste affiche tous les quarts existants avec leurs horaires.',
            'Pour créer : cliquez sur "+ Ajouter", renseignez le nom, l\'heure de debut et l\'heure de fin.',
            'Pour modifier : cliquez sur l\'icone crayon a cote du quart.',
            'Pour supprimer : cliquez sur l\'icone corbeille (bloque si des employés sont assignes).'
          ],
          result: 'Le quart est disponible pour l\'assignation aux employés et la planification.',
          tips: [
            'Pour les quarts de nuit traversant minuit (ex: 23h-7h), indiquez simplement 23:00 comme debut et 07:00 comme fin. Le systeme gere automatiquement le passage a minuit.',
            'Créez des quarts avec des noms parlants : "Jour", "Soir", "Nuit" plutot que "Q1", "Q2", "Q3".'
          ],
          errors: [
            { problem: 'Suppression bloquee', solution: 'Des employés sont assignes a ce quart. Reassignez-les avant de supprimer.' }
          ]
        }
      ]
    },

    /* ============================================================
       5. PLANIFICATION
    ============================================================ */
    {
      id: 'planification',
      title: 'Planification',
      overview: 'Planifiez les horaires de vos employés sur plusieurs semaines avec une vue calendrier interactive.',
      content: [
        {
          id: 'vue-planification',
          title: 'Vue de planification',
          description: 'La page Planification présente un calendrier hebdomadaire interactif. Chaque case représente un employé pour un jour donne. Vous pouvez assigner des quarts par simple clic.',
          whenToUse: 'Pour planifier les horaires de la semaine a venir ou vérifier la planification en cours.',
          steps: [
            'Cliquez sur "Planification" dans le menu lateral.',
            'Sélectionnez la semaine avec les fleches de navigation.',
            'Chaque ligne représente un employé, chaque colonne un jour.',
            'Cliquez sur une case vide pour assigner un quart.',
            'Sélectionnez le quart dans le menu deroulant qui apparait.',
            'Le quart est immédiatement affiche dans la case avec un code couleur.'
          ],
          screenshot: '/docs/scheduling-view.png',
          screenshotAlt: 'Vue de planification hebdomadaire',
          result: 'La planification est enregistree et utilisee pour le calcul des retards et absences.',
          tips: [
            'Utilisez la copie multi-semaines pour dupliquer une planification sur plusieurs semaines.',
            'Les opérations en masse permettent d\'assigner le meme quart a plusieurs employés en un clic.'
          ]
        },
        {
          id: 'copie-multi-semaines',
          title: 'Copie multi-semaines',
          description: 'Dupliquez la planification de la semaine en cours sur une ou plusieurs semaines suivantes pour éviter la saisie répétitive.',
          whenToUse: 'Lorsque les horaires sont stables et se répètent d\'une semaine a l\'autre.',
          steps: [
            'Configurez la planification de la semaine de référence.',
            'Cliquez sur le bouton "Copier" en haut de la page.',
            'Sélectionnez le nombre de semaines a copier.',
            'Confirmez l\'opération.',
            'La planification est dupliquee sur les semaines suivantes.'
          ],
          result: 'La planification est copiee identiquement sur les semaines choisies. Vous pouvez ensuite ajuster au cas par cas.',
          warnings: ['La copie ecrase les planifications existantes sur les semaines cibles. Vérifiez qu\'il n\'y a pas de planifications spécifiques déjà saisies.']
        }
      ]
    },

    /* ============================================================
       6. RAPPORTS JOURNALIERS
    ============================================================ */
    {
      id: 'rapports',
      title: 'Rapports journaliers',
      overview: 'Consultez et validez les pointages du jour avec un workflow complet d\'approbation.',
      content: [
        {
          id: 'consulter-rapports',
          title: 'Consulter les rapports',
          description: 'La page Rapports affiche les pointages d\'une journee selectionnee avec les heures d\'arrivee, de depart, la duree travaillee et le statut de validation.',
          whenToUse: 'Quotidiennement pour vérifier et valider les heures travaillees de la veille.',
          steps: [
            'Cliquez sur "Rapports" dans le menu lateral.',
            'Sélectionnez la date du jour a consulter via le selecteur de date en haut.',
            'Les KPIs compacts montrent : Total pointages, Valides, En attente, Rejetes, Heures totales.',
            'Filtrez par recherche, site, zone ou onglet de validation (Tous/En attente/Valides/Rejetes).',
            'Chaque ligne du tableau montre : employé, heure arrivee, heure depart, duree, statut.'
          ],
          screenshot: '/docs/reports-daily.png',
          screenshotAlt: 'Rapport journalier avec pointages',
          result: 'Vous visualisez tous les pointages du jour avec leurs statuts de validation.',
          tips: ['Utilisez le bouton "Valider la journee" en haut pour sélectionner et approuver tous les pointages en attente d\'un seul coup.']
        },
        {
          id: 'valider-pointages',
          title: 'Valider les pointages',
          description: 'Le processus de validation permet d\'approuver ou rejeter chaque pointage individuellement ou en masse.',
          whenToUse: 'Apres chaque journee de travail pour confirmer les heures avant leur transfert en paie.',
          steps: [
            'Dans le rapport journalier, les pointages "En attente" sont affiches en ambre.',
            'Pour valider individuellement : cliquez sur la ligne pour ouvrir le panneau lateral.',
            'Le panneau affiche : resume (entree, sortie, duree), statut de validation, photos de pointage, timeline.',
            'Cliquez sur "Approuver" (vert) pour valider ou "Rejeter" (rouge) pour refuser.',
            'En cas de rejet, un commentaire obligatoire est demande pour justifier la decision.',
            'Pour valider en masse : cochez les cases des pointages a valider, puis cliquez sur "Approuver" ou "Rejeter" dans la barre d\'actions.'
          ],
          result: 'Les pointages valides sont marqués en vert avec le nom du validateur et la date de validation. Les pointages rejetes sont marques en rouge.',
          warnings: [
            'Le rejet nécessite obligatoirement un commentaire. Cela garantit la traçabilité des decisions.',
            'Une fois validé, un pointage ne peut plus etre modifié sauf par un administrateur.'
          ]
        },
        {
          id: 'exports-rapports',
          title: 'Exports des rapports',
          description: 'Exportez les rapports journaliers en format Excel ou PDF pour archivage ou communication.',
          whenToUse: 'Pour conserver un historique papier, envoyer a la comptabilite ou a un auditeur.',
          steps: [
            'Sur la page Rapports, cliquez sur le bouton "Excel" ou "PDF" en haut a droite.',
            'Le fichier se telecharge automatiquement avec les donnees filtrees actuellement affichees.',
            'L\'export Excel inclut toutes les colonnes du tableau.',
            'L\'export PDF est formate pour l\'impression avec un en-tete contenant la date et les filtres appliques.'
          ],
          result: 'Un fichier Excel ou PDF est telecharge avec les donnees du rapport courant.'
        }
      ]
    },

    /* ============================================================
       7. MODULE PAIE / RH
    ============================================================ */
    {
      id: 'module-paie',
      title: 'Module Paie / RH',
      overview: 'Gerez les périodes de paie, validez les feuilles de temps et exportez les donnees pour la comptabilite.',
      content: [
        {
          id: 'périodes-paie',
          title: 'Periodes de paie',
          description: 'Les périodes de paie definissent les intervalles pour le calcul des heures (generalement bimensuel ou mensuel). Chaque période a un statut : Ouverte, Fermee ou Verrouillée.',
          whenToUse: 'Configurez les périodes de paie lors du setup initial, puis créez une nouvelle période pour chaque cycle de paie.',
          steps: [
            'Cliquez sur "Paie / RH" dans le menu lateral.',
            'Le resume visuel en haut montre la période courante avec ses statistiques.',
            'Pour créer une période : cliquez sur "+ Nouvelle période", definissez les dates de debut et fin.',
            'Une seule période peut etre "Ouverte" a la fois. Les anciennes doivent etre fermees/verrouillées.',
            'Le systeme généré automatiquement les feuilles de temps pour chaque employé de la période.'
          ],
          screenshot: '/docs/payroll-periods.png',
          screenshotAlt: 'Module Paie - Periodes et resume',
          result: 'La période est créée et les feuilles de temps sont générées automatiquement.',
          tips: ['Vérifiez les alertes pre-verrouillage avant de verrouiller une période. Elles signalent les anomalies a corriger.']
        },
        {
          id: 'feuilles-temps',
          title: 'Feuilles de temps',
          description: 'Les feuilles de temps récapitulent les heures de chaque employé pour une période de paie. Elles incluent : heures régulières, heures supplémentaires, retards, absences et pauses.',
          whenToUse: 'Pour vérifier les heures de chaque employé avant le verrouillage de la période.',
          steps: [
            'Dans le module Paie, sélectionnez une période pour voir ses feuilles de temps.',
            'Chaque ligne montre : employé, heures régulières, heures supplémentaires, retards, absences, total.',
            'Cliquez sur une ligne pour voir le détail jour par jour.',
            'Utilisez les filtres pour trouver un employé spécifique.',
            'Validez chaque feuille individuellement ou en masse.'
          ],
          result: 'Vous avez une vue détaillée des heures de chaque employé pour la période selectionnee.'
        },
        {
          id: 'validation-paie',
          title: 'Validation en masse de la paie',
          description: 'Approuvez ou rejetez plusieurs feuilles de temps simultanement pour accelerer le processus de validation.',
          whenToUse: 'Avant le verrouillage d\'une période de paie, pour valider toutes les feuilles en attente.',
          steps: [
            'Dans l\'onglet Validation du module Paie, cochez les feuilles de temps a traiter.',
            'Utilisez "Tout sélectionner" pour sélectionner toutes les feuilles en attente.',
            'Cliquez sur "Approuver (N)" pour valider ou "Rejeter (N)" pour refuser.',
            'Une boite de confirmation s\'affiche avec le récapitulatif.',
            'Ajoutez un commentaire optionnel et confirmez.',
            'Les périodes verrouillées sont automatiquement ignorees.'
          ],
          result: 'Toutes les feuilles selectionnees sont validees/rejetees en une seule opération.',
          warnings: ['Les feuilles de périodes verrouillées ne peuvent pas etre modifiees. Deverrouillez d\'abord la période si nécessaire.']
        },
        {
          id: 'verrouillage',
          title: 'Verrouillage des périodes',
          description: 'Le verrouillage fige definitivement les donnees d\'une période de paie. Aucune modification n\'est possible apres verrouillage.',
          whenToUse: 'Lorsque toutes les feuilles de temps sont validees et que les donnees sont pretes pour le traitement comptable.',
          steps: [
            'Assurez-vous que toutes les feuilles de temps sont validees.',
            'Vérifiez les alertes pre-verrouillage (anomalies detectees par le systeme).',
            'Cliquez sur "Verrouiller la période".',
            'Confirmez l\'action. Cette opération est irréversible.',
            'La période passe au statut "Verrouillée".'
          ],
          result: 'La période est figee. Les donnees sont pretes pour l\'export comptable.',
          warnings: [
            'Le verrouillage est irréversible. Vérifiez toutes les donnees avant de procéder.',
            'Les alertes pre-verrouillage signalent les anomalies (heures manquantes, pointages incomplets). Corrigez-les avant de verrouiller.'
          ]
        },
        {
          id: 'exports-paie',
          title: 'Exports paie',
          description: 'Exportez les donnees de paie en CSV, Excel ou PDF pour intégration dans votre systeme comptable.',
          whenToUse: 'Apres le verrouillage d\'une période pour transmettre les donnees a la comptabilite.',
          steps: [
            'Dans le module Paie, sélectionnez la période a exporter.',
            'Cliquez sur le format d\'export souhaite : CSV, Excel ou PDF.',
            'CSV : format tabulaire brut, ideal pour l\'import dans un logiciel de paie.',
            'Excel : format structuré avec mise en forme, ideal pour la verification manuelle.',
            'PDF : format de presentation, ideal pour l\'archivage et la signature.',
            'Le fichier se telecharge automatiquement.'
          ],
          result: 'Les donnees de paie sont exportees dans le format choisi, pretes pour le traitement comptable.'
        }
      ]
    },

    /* ============================================================
       8. ANALYTIQUE
    ============================================================ */
    {
      id: 'analytique',
      title: 'Analytique',
      overview: 'Tableaux de bord analytiques avances avec KPIs, graphiques, comparaisons et exports.',
      content: [
        {
          id: 'resume-exécutif',
          title: 'Resume exécutif',
          description: 'La page Analytique affiche 4 KPIs principaux avec comparaison par rapport a la période précédente : taux de présence, heures totales, retards, heures supplémentaires.',
          whenToUse: 'Pour une vue strategique des indicateurs RH et identifier les tendances.',
          steps: [
            'Cliquez sur "Analytique" dans le menu lateral.',
            'Les 4 cartes KPI en haut montrent les indicateurs cles avec des badges delta (evolution).',
            'Vert = amelioration par rapport a la période précédente.',
            'Rouge = dégradation.',
            'La section "Insights" généré automatiquement des alertes basees sur les donnees.'
          ],
          screenshot: '/docs/analytics-overview.png',
          screenshotAlt: 'Page Analytique - Resume exécutif',
          result: 'Vous avez une vue strategique de la performance RH avec les tendances.',
          tips: ['Sélectionnez differentes périodes pour comparer les performances au fil du temps.']
        },
        {
          id: 'graphiques',
          title: 'Graphiques et visualisations',
          description: 'Plusieurs graphiques interactifs visualisent les donnees : tendance de présence, repartition par statut, heures par département, comparaison de périodes.',
          whenToUse: 'Pour presenter des donnees visuelles en reunion ou identifier des patterns.',
          steps: [
            'Descendez sous les KPIs pour accéder aux graphiques.',
            'Graphique Tendance : evolution de la présence sur la période selectionnee (courbe).',
            'Graphique Repartition : distribution des statuts (présent, absent, retard) (camembert).',
            'Graphique Heures : heures travaillees par département (barres).',
            'Graphique Comparaison : comparaison entre deux périodes (barres groupees).',
            'Le Tableau Employés en bas permet un tri par nombre d\'heures, retards ou absences.'
          ],
          result: 'Des visualisations claires pour la prise de decision et le reporting RH.'
        }
      ]
    },

    /* ============================================================
       9. ALERTES ET ABSENCES
    ============================================================ */
    {
      id: 'alertes',
      title: 'Alertes et absences',
      overview: 'Gerez les alertes de retard, d\'absence et d\'anomalie. Traitez-les individuellement ou en masse.',
      content: [
        {
          id: 'comprendre-alertes',
          title: 'Comprendre les alertes',
          description: 'Le systeme généré automatiquement des alertes lorsqu\'il détecté un retard, une absence non justifiée ou une anomalie de pointage. Les alertes apparaissent dans le Dashboard et dans le module dédié.',
          whenToUse: 'Consultez les alertes quotidiennement pour maintenir un suivi rigoureux.',
          steps: [
            'Les alertes sont visibles dans le Dashboard (section Actions prioritaires) et dans l\'onglet Alertes de la page rapports.',
            'Chaque alerte montre : type, employé, date/heure, site/zone, statut (non traitée/traitée).',
            'Types d\'alertes : Retard, Absence, Anomalie pointage, Depart anticipe.',
            'Les alertes non traitées restent actives jusqu\'a ce qu\'elles soient traitées.'
          ],
          result: 'Vous avez une liste complete des anomalies a traiter.'
        },
        {
          id: 'traiter-alertes',
          title: 'Traiter les alertes',
          description: 'Traitez chaque alerte en lui assignant un motif : justifiée, injustifiée, jour ferie, conge, maladie, etc.',
          whenToUse: 'Lorsque vous avez identifié la cause d\'un retard ou d\'une absence.',
          steps: [
            'Cliquez sur une alerte pour ouvrir le détail.',
            'Sélectionnez le motif de traitement dans la liste deroulante.',
            'Ajoutez un commentaire si nécessaire.',
            'Cliquez sur "Traiter" pour confirmer.',
            'L\'alerte passe au statut "Traitée" et disparait des alertes actives.'
          ],
          result: 'L\'alerte est traitée, documentée et archivee.',
          tips: ['Les motifs disponibles incluent : Justifiee, Injustifiee, Jour ferie, Absence collective, Conge, Maladie, Formation.']
        },
        {
          id: 'traitement-masse-alertes',
          title: 'Traitement en masse des alertes',
          description: 'Sélectionnez plusieurs alertes pour les traiter simultanement avec le meme motif.',
          whenToUse: 'Pour les jours feries ou les absences collectives touchant plusieurs employés.',
          steps: [
            'Dans la liste des alertes, cochez les cases des alertes a traiter.',
            'Utilisez "Tout sélectionner" pour sélectionner toutes les alertes visibles.',
            'Cliquez sur "Traiter (N)" dans la barre d\'actions groupees.',
            'Sélectionnez le motif commun (ex: "Jour ferie").',
            'Ajoutez un commentaire optionnel et un avertissement si nécessaire.',
            'Confirmez. Toutes les alertes selectionnees sont traitées en une seule opération.'
          ],
          screenshot: '/docs/alerts-bulk-treat.png',
          screenshotAlt: 'Traitement en masse des alertes',
          result: 'Toutes les alertes selectionnees sont traitées avec le meme motif. L\'opération est journalisee.',
          tips: ['Le traitement en masse est particulierement utile pour les jours feries ou les fermetures temporaires qui generent de nombreuses alertes d\'absence simultanees.']
        }
      ]
    },

    /* ============================================================
       10. LISTES DE REFERENCE
    ============================================================ */
    {
      id: 'listes-référence',
      title: 'Listes de référence',
      overview: 'Gerez les donnees de référence utilisees dans toute l\'application : postes, départements, types d\'absence, etc.',
      content: [
        {
          id: 'gerer-lookups',
          title: 'Gerer les listes de référence',
          description: 'Les listes de référence (lookups) sont les valeurs predefinies utilisees dans les formulaires : postes, départements, types d\'absence, motifs de retard, etc.',
          whenToUse: 'Lors de la configuration initiale pour definir les valeurs disponibles, ou pour ajouter de nouvelles valeurs.',
          steps: [
            'Cliquez sur "Listes" dans le menu lateral.',
            'Sélectionnez la catégorie de liste a gerer (Postes, Départements, etc.).',
            'Chaque élément affiche un indicateur d\'utilisation (nombre d\'employés/donnees qui l\'utilisent).',
            'Pour ajouter : cliquez sur "+ Ajouter", saisissez le libelle.',
            'Pour modifier : cliquez sur l\'icone crayon.',
            'Pour desactiver : cliquez sur le toggle actif/inactif.',
            'Pour supprimer : cliquez sur l\'icone corbeille (bloque si l\'élément est utilise).'
          ],
          result: 'Les valeurs de référence sont mises a jour et disponibles dans tous les formulaires de l\'application.',
          warnings: ['Un élément utilise par des employés ou des donnees ne peut pas etre supprimé. Desactivez-le plutot pour le masquer des formulaires tout en conservant les références existantes.'],
          tips: ['L\'indicateur d\'utilisation vous aide a identifier les éléments obsoletes que vous pouvez desactiver en toute sécurité.']
        }
      ]
    },

    /* ============================================================
       11. PARAMETRES
    ============================================================ */
    {
      id: 'paramètres',
      title: 'Paramètres',
      overview: 'Configurez les paramètres de votre organisation : regles de pointage, heures supplémentaires, seuils d\'alerte.',
      content: [
        {
          id: 'paramètres-généraux',
          title: 'Paramètres généraux',
          description: 'Les paramètres definissent les regles de fonctionnement de votre organisation dans PunchTM.',
          whenToUse: 'Lors de la configuration initiale et chaque fois que les regles changent.',
          steps: [
            'Cliquez sur "Paramètres" dans le menu lateral.',
            'Les sections sont organisees par theme : General, Pointage, Paie, Alertes.',
            'Modifiez les valeurs selon vos besoins.',
            'Cliquez sur "Enregistrer" pour sauvegarder.',
            'Les changements prennent effet immédiatement.'
          ],
          result: 'Les paramètres sont appliques a toute l\'organisation.',
          tips: ['Le Dark Mode (mode sombre) est accessible depuis le bouton lune/soleil dans le menu lateral, pas dans les paramètres.']
        }
      ]
    },

    /* ============================================================
       12. CAS PRATIQUES
    ============================================================ */
    {
      id: 'cas-pratiques',
      title: 'Cas pratiques',
      overview: 'Procedures detaillees pour les situations courantes en gestion RH.',
      content: [
        {
          id: 'cas-jour-ferie',
          title: 'Cas : Jour ferie',
          description: 'Comment gerer un jour ferie dans PunchTM lorsque des alertes d\'absence sont générées pour les employés qui ne travaillent pas.',
          whenToUse: 'Chaque jour ferie ou fermeture exceptionnelle de l\'entreprise.',
          steps: [
            'Le lendemain du jour ferie, PunchTM aura généré des alertes d\'absence pour tous les employés planifies qui n\'ont pas pointe.',
            'Allez dans le module Alertes/Absences.',
            'Cochez "Tout sélectionner" pour sélectionner toutes les alertes du jour ferie.',
            'Cliquez sur "Traiter en masse".',
            'Sélectionnez le motif "Jour ferie".',
            'Ajoutez un commentaire : "Jour ferie national - [nom du jour ferie]".',
            'Confirmez. Toutes les alertes sont traitées en une opération.'
          ],
          result: 'Toutes les absences du jour ferie sont justifiées. Elles n\'impacteront pas les statistiques d\'absenteisme.',
          tips: ['Traitez les jours feries des le lendemain pour éviter que les alertes ne s\'accumulent dans le tableau de bord.']
        },
        {
          id: 'cas-absence-justifiée',
          title: 'Cas : Absence justifiée',
          description: 'Comment traiter une absence avec justificatif (certificat medical, convocation, etc.).',
          whenToUse: 'Lorsqu\'un employé fournit un justificatif pour son absence.',
          steps: [
            'Localisez l\'alerte d\'absence de l\'employé dans le module Alertes.',
            'Cliquez sur l\'alerte pour ouvrir le détail.',
            'Sélectionnez le motif correspondant : "Maladie", "Conge" ou "Formation".',
            'Ajoutez un commentaire avec la référence du justificatif : "Certificat medical du Dr. X, ref. 12345".',
            'Cliquez sur "Traiter".'
          ],
          result: 'L\'absence est justifiée et documentée. Le commentaire sert de trace pour l\'audit.',
          tips: ['Documentez toujours la référence du justificatif dans le commentaire pour faciliter les audits futurs.']
        },
        {
          id: 'cas-correction-erreur',
          title: 'Cas : Correction d\'erreur de pointage',
          description: 'Comment corriger un pointage errone (mauvaise heure, pointage manquant, double pointage).',
          whenToUse: 'Lorsqu\'un employé signale une erreur dans ses heures ou qu\'une anomalie est détectée.',
          steps: [
            'Allez dans les Rapports journaliers et sélectionnez la date concernee.',
            'Trouvez l\'employé et cliquez sur sa ligne pour ouvrir le panneau de détails.',
            'Consultez la timeline des pointages pour identifier l\'anomalie.',
            'Si un pointage est manquant : l\'administrateur peut ajouter un pointage manuel.',
            'Si un pointage est errone : rejetez le pointage avec un commentaire explicatif.',
            'Si c\'est un double pointage : rejetez le doublon et validez le pointage correct.'
          ],
          result: 'Le pointage est corrige et les heures calculees refletent la realite.',
          warnings: ['Toute correction est tracee dans le journal d\'audit. Documentez toujours la raison de la correction.']
        }
      ]
    }
  ]
};
